import { db } from "@db";
import { decodeAminoAcids, decodeNucleotides } from "@db/encoding";
import type { SequenceKind } from "@lib/genetics";
import { reverseComplement } from "@lib/genetics";

/**
 * Yield decoded sequence strings one chunk at a time from IndexedDB.
 *
 * Each yielded string is a decoded segment of the sequence (~1MB). The full
 * sequence is never assembled in memory.
 */
export async function* iterateDecodedChunks(
  sequenceId: string,
  kind: SequenceKind,
  length: number,
  chunkSize: number,
): AsyncGenerator<string> {
  const chunks = await db.sequenceChunks
    .where("[sequenceId+index]")
    .between([sequenceId, -Infinity], [sequenceId, Infinity])
    .sortBy("index");

  for (let i = 0; i < chunks.length; i++) {
    if (kind === "aa") {
      yield decodeAminoAcids(chunks[i].data);
    } else {
      const chunkLength = Math.min(chunkSize, length - i * chunkSize);
      yield decodeNucleotides(chunks[i].data, chunkLength);
    }
  }
}

/**
 * Yield decoded sequence strings in reverse-complement order.
 *
 * Chunks are iterated in reverse, and each decoded chunk is reversed and
 * complemented. This produces the full reverse complement without assembling
 * the entire sequence.
 */
export async function* iterateDecodedChunksRC(
  sequenceId: string,
  length: number,
  chunkSize: number,
): AsyncGenerator<string> {
  const chunks = await db.sequenceChunks
    .where("[sequenceId+index]")
    .between([sequenceId, -Infinity], [sequenceId, Infinity])
    .sortBy("index");

  for (let i = chunks.length - 1; i >= 0; i--) {
    const chunkLength = Math.min(chunkSize, length - i * chunkSize);
    const decoded = decodeNucleotides(chunks[i].data, chunkLength);
    yield reverseComplement(decoded);
  }
}
